// Shared disclaimer text + canvas baker used by every AI-generated image
// surface (DoozyGo Magic + AI Party Planner + Admin / CRM viewers).
//
// Design notes:
//   - The DOM overlay (<AIImageWithDisclaimer>) handles the on-page preview.
//   - For downloads / native-shares, we bake the same text onto the bitmap
//     via canvas so the disclaimer travels with the file. All processing is
//     client-side — no extra AI calls, no re-generation, no storage changes.
//   - If the canvas baker ever fails (CORS, OOM, etc.) we gracefully return
//     the original source so downloads never break.

export const AI_DISCLAIMER_TEXT =
  "This is an AI-generated reference image for illustrative purposes only. Terms and Conditions apply.";

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const wrapLines = (ctx, text, maxWidth) => {
  const words = text.split(" ");
  const lines = [];
  let current = "";
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines;
};

/**
 * Return a data URL identical to `src` but with the AI disclaimer baked
 * onto a translucent band at the bottom of the image.
 *
 * @param {string} src  Full data: URL or http(s) URL. Base64 strings are
 *                      NOT accepted — callers should wrap with the correct
 *                      MIME prefix first (keeps JPEG vs PNG explicit).
 * @param {object} opts { mime?: 'image/jpeg'|'image/png', quality?: number }
 * @returns {Promise<string>} data URL. Falls back to the input on error.
 */
export async function bakeDisclaimerOnImage(src, opts = {}) {
  if (!src) return src;
  const mime = opts.mime || (src.startsWith("data:image/png") ? "image/png" : "image/jpeg");
  const quality = typeof opts.quality === "number" ? opts.quality : 0.92;
  try {
    const img = await loadImage(src);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return src;

    // Measure the disclaimer first (needs a context) so we can append it as its
    // OWN strip BELOW the image — this guarantees it never overlaps the pink
    // DoozyGo brand footer that the backend bakes into the bottom of the image.
    const measure = document.createElement("canvas").getContext("2d");
    const fontSize = Math.max(11, Math.round(w * 0.0145));
    const padX = Math.round(w * 0.03);
    const padY = Math.max(7, Math.round(fontSize * 0.8));
    measure.font = `400 ${fontSize}px Inter, -apple-system, Arial, sans-serif`;
    const lines = wrapLines(measure, AI_DISCLAIMER_TEXT, w - 2 * padX);
    const lineHeight = Math.round(fontSize * 1.3);
    const bandHeight = lines.length * lineHeight + 2 * padY;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h + bandHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    // Dedicated disclaimer strip beneath the artwork (transparent black ribbon).
    ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
    ctx.fillRect(0, h, w, bandHeight);

    ctx.font = `400 ${fontSize}px Inter, -apple-system, Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
    const startY = h + padY + lineHeight / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, w / 2, startY + i * lineHeight);
    });

    return canvas.toDataURL(mime, quality);
  } catch (err) {
    // Common cause: canvas tainted by cross-origin image. Fall back silently
    // so the download still succeeds — the DOM overlay already shows the
    // disclaimer on screen.
    if (typeof console !== "undefined") console.debug("[aiDisclaimer] bake failed:", err?.message);
    return src;
  }
}

/** Convenience: accept raw base64 + mime, return baked data URL. */
export async function bakeDisclaimerOnBase64(b64, mime = "image/jpeg") {
  if (!b64) return "";
  const src = b64.startsWith("data:") ? b64 : `data:${mime};base64,${b64}`;
  return bakeDisclaimerOnImage(src, { mime });
}
